import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const fmt = {
  bytes: (b) => {
    if (!b) return '0 B';
    if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
    if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024).toFixed(1)} KB`;
  },
  uptime: (ms) => {
    if (!ms) return '—';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${s % 60}s`;
  },
};

function SectionHeader({ title }) {
  return (
    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '0.75rem', letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px', marginTop: '0' }}>
      {title}
    </h2>
  );
}

function StatRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontFamily: mono ? 'monospace' : 'inherit' }}>{value ?? '—'}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const online = status === 'online';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '2px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600',
      background: online ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
      color: online ? '#4ade80' : '#f87171',
      border: `1px solid ${online ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}`,
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: online ? '#4ade80' : '#f87171', display: 'inline-block' }} />
      {status ?? 'unknown'}
    </span>
  );
}

function JobBadge({ status }) {
  const ok = status === 'success';
  const color = ok ? '#4ade80' : status === 'no runs recorded' || status === 'no log found' ? '#a0a0a0' : '#f87171';
  const bg    = ok ? 'rgba(74,222,128,0.12)' : status === 'no runs recorded' || status === 'no log found' ? 'rgba(160,160,160,0.1)' : 'rgba(248,113,113,0.12)';
  const bdr   = ok ? 'rgba(74,222,128,0.3)'  : status === 'no runs recorded' || status === 'no log found' ? 'rgba(160,160,160,0.2)' : 'rgba(248,113,113,0.3)';
  return (
    <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600', background: bg, color, border: `1px solid ${bdr}` }}>
      {status ?? '—'}
    </span>
  );
}

export default function AdminSystem() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get('/api/admin/system');
      setData(r.data);
      setLastRefresh(new Date());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // auto-refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const { app, database, jobs, errors } = data || {};

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">SYSTEM</h1>
          <p className="page-subtitle">Application health and operational status</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {lastRefresh && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-outline btn-sm" onClick={fetchData} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>

          {/* ── Top row: API + Database side by side ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

            {/* API Process */}
            <div className="card">
              <SectionHeader title="API Process" />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.95rem' }}>wowfactor-api</span>
                <StatusBadge status={app?.status} />
              </div>
              <StatRow label="Uptime"        value={fmt.uptime(app?.uptime_ms)} />
              <StatRow label="Memory"        value={fmt.bytes(app?.memory_bytes)} />
              <StatRow label="CPU"           value={app != null ? `${app.cpu_percent}%` : '—'} />
              <StatRow label="Restarts"      value={app?.restarts} />
              <StatRow label="Node.js"       value={app?.node_version} mono />
              <StatRow label="PID"           value={app?.pid} mono />
            </div>

            {/* Database */}
            <div className="card">
              <SectionHeader title="Database" />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.95rem' }}>wowfactor</span>
                <StatusBadge status="online" />
              </div>
              <StatRow label="Total Size"        value={database?.size} />
              <StatRow label="DB Connections"    value={database?.connections ? `${database.connections.active} active / ${database.connections.total} total` : '—'} />
            </div>
          </div>

          {/* ── Table row counts ── */}
          <div className="card">
            <SectionHeader title="Table Row Counts" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0' }}>
              {database?.tables?.map(t => (
                <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px 7px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{t.name}</span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: '600', minWidth: '32px', textAlign: 'right' }}>{t.rows}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Scheduled Jobs ── */}
          <div className="card">
            <SectionHeader title="Scheduled Jobs" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>

              {/* Backup */}
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-primary)' }}>Database Backup</span>
                  <JobBadge status={jobs?.backup?.status} />
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Schedule: <span style={{ color: 'var(--text-secondary)' }}>Daily at 2:00 AM</span>
                </div>
                {jobs?.backup?.timestamp && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Last run: <span style={{ color: 'var(--text-secondary)' }}>{jobs.backup.timestamp}</span>
                  </div>
                )}
                {jobs?.backup?.last_file && (
                  <>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '2px', marginTop: '6px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {jobs.backup.last_file.filename}
                    </div>
                    {jobs.backup.last_file.size_kb && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {jobs.backup.last_file.size_kb} KB compressed
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Cleanup */}
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-primary)' }}>Token Cleanup</span>
                  <JobBadge status={jobs?.cleanup?.status} />
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Schedule: <span style={{ color: 'var(--text-secondary)' }}>Daily at 3:00 AM</span>
                </div>
                {jobs?.cleanup?.timestamp && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Last run: <span style={{ color: 'var(--text-secondary)' }}>{jobs.cleanup.timestamp}</span>
                  </div>
                )}
                {!jobs?.cleanup?.timestamp && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Removes expired tokens from the database
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* ── Recent Errors ── */}
          <div className="card">
            <SectionHeader title="Recent API Errors" />
            {!errors?.length ? (
              <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <span style={{ fontSize: '1.5rem' }}>✓</span>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '6px' }}>No recent errors</p>
              </div>
            ) : (
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '12px 14px', maxHeight: '260px', overflowY: 'auto' }}>
                {errors.map((line, i) => (
                  <div key={i} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: line.includes('error') || line.includes('Error') ? '#f87171' : 'var(--text-secondary)', padding: '2px 0', borderBottom: i < errors.length - 1 ? '1px solid var(--border)' : 'none', wordBreak: 'break-all', lineHeight: '1.5' }}>
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
